"use client";

import { User, UserAttributes } from "@supabase/supabase-js";
import React, { createContext, useContext, ReactNode, useEffect, useCallback } from "react";
import { useSupabase } from "./supabase-provider";
import { redirect } from "next/navigation";
import { paths } from "@src/utils";
import { reportError } from "@lib/utils/reportError";
import { posthog } from "@src/analytics";
import { config } from "@lib/utils/config";
import { log } from "@lib/utils/log";
interface AuthContextValue {
  user: User | null;
  updateUser: (attributes: UserAttributes) => Promise<string>;
  loading: boolean;
  signOut: (skipRedirect?: boolean) => Promise<void>;
  isSuperuser: boolean;
  superuserModeEnabled: boolean;
  toggleSuperuserMode: () => void;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: ReactNode;
  user?: User | null; // If user is not specified, it should be fetched
  isSuperuser?: boolean; // Superuser status from server
}> = ({
  user,
  isSuperuser = false,
  children
}) => {
  const {
    supabase
  } = useSupabase();
  const [state, setState] = React.useState<{
    user: User | null;
    loading: boolean;
  }>({
    user: user || null,
    loading: user === undefined
  });

  // Superuser mode toggle (client-side state)
  const [superuserModeEnabled, setSuperuserModeEnabled] = React.useState<boolean>(false);
  const toggleSuperuserMode = React.useCallback(() => {
    setSuperuserModeEnabled(prev => !prev);
  }, []);
  const signOut = useCallback(async (skipRedirect: boolean = false) => {
    await supabase.auth.signOut();

    // Reset PostHog on sign out
    if (config.posthogEnabled) {
      posthog.reset();
    }
    if (!skipRedirect) {
      redirect(paths.root);
    }
  }, [supabase]);
  const updateUser = useCallback(async (attributes: UserAttributes): Promise<string> => {
    if (attributes.data && attributes.data["organization"] === "") {
      attributes.data["organization"] = null;
    }
    const {
      data
    } = await supabase.auth.updateUser(attributes);
    if (!data.user) {
      throw new Error("Missing user");
    }
    setState({
      user: data.user,
      loading: false
    });
    return data.user.id;
  }, [supabase]);
  useEffect(() => {
    // Capture current superuser status to avoid stale ref in setTimeout
    const currentIsSuperuser = isSuperuser;
    const {
      data: authListener
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        // Use this to defer the getUser call until after the callback has finished
        // Without this, it's possible to trigger a deadlock with other supabase functions
        // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
        setTimeout(async () => {
          const {
            data: {
              user
            },
            error
          } = await supabase.auth.getUser();
          if (error) {
            reportError(error);
          }
          setState({
            user: user || null,
            loading: false
          });

          // Identify user in PostHog with superuser status
          if (user && config.posthogEnabled) {
            log("Updating posthog identity");
            posthog.identify(user.id, {
              email: user.email,
              name: user.user_metadata?.name,
              created_at: user.created_at,
              is_superuser: currentIsSuperuser
            });
          }
        }, 0);
      } else {
        setState({
          user: null,
          loading: false
        });

        // Reset PostHog on sign out
        if (config.posthogEnabled) {
          posthog.reset();
        }
      }
    });
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [supabase, isSuperuser]);
  return <AuthContext.Provider value={{
    ...state,
    signOut,
    updateUser,
    isSuperuser,
    superuserModeEnabled,
    toggleSuperuserMode
  }} data-sentry-element="AuthContext.Provider" data-sentry-component="AuthProvider" data-sentry-source-file="auth-provider.tsx">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};